import React, { useState, useRef } from 'react';
import {
  MessageSquare,
  ExternalLink,
  RotateCcw,
  Paperclip,
  X,
  ChevronRight,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { JumpToLatest, Spinner } from './ui';
import { useStickToBottom } from '../hooks/useStickToBottom';
import MarkdownMessage from './MarkdownMessage';
import ExpandableToolPill from './ExpandableToolPill';
import EventCard from './EventCard';
import PendingJobCard from './PendingJobCard';
import ActionSuggestionCard from './ActionSuggestionCard';

import {
  isUploadExtracting,
  type AgentMessage,
  type AgentUpload,
  type ContentSegment,
  type ViewSuggestion,
  type ActionSuggestion,
  type PendingJob,
} from '../hooks/useAgentChat';

export type AgentChatVariant = 'drawer' | 'full';

export interface AgentChatViewProps {
  variant: AgentChatVariant;
  messages: AgentMessage[];
  isStreaming: boolean;
  viewSuggestions: ViewSuggestion[];
  actionSuggestions: ActionSuggestion[];
  pendingJobs: PendingJob[];
  /** Files uploaded to this conversation (rendered as chips above the input). */
  uploads?: AgentUpload[];
  /** Flip an upload between "Program context" and "Chat only". Omit to render read-only chips. */
  onToggleUploadContext?: (uploadId: string, useAsProgramContext: boolean) => void;
  onSend: (text: string, files?: File[]) => void;
  onUndo: (editId: string) => void;
  onDismissSuggestion: (index: number) => void;
  onDismissActionSuggestion: (index: number) => void;
  onNavigateToView: (suggestion: ViewSuggestion) => void;
  emptyState?: React.ReactNode;
  disabled?: boolean;
}

interface VariantStyles {
  messageSize: 'sm' | 'base';
  compactSubComponents: boolean;
  /** Wrapper for a user message (width + radius + padding + colors). */
  userBubbleClass: string;
  /** Wrapper for an assistant message — bubble-less in the drawer, carded in full. */
  assistantBubbleClass: string;
  outerContainerClass: string;
  messagesInnerWrapper: string;
  messagesSpacing: string;
  emptyStateDefault: React.ReactNode;
  centerEventCards: boolean;
  suggestionsPanelClass: string;
  inputAreaClass: string;
  inputInnerWrapper: string;
  textareaClass: string;
  textareaMinHeight: number;
  textareaMaxHeight: number;
  textareaPlaceholder: string;
  dragOverlayTextClass: string;
  timestampMarginClass: string;
  attachmentsMarginClass: string;
  attachmentChipClass: string;
  fileChipOuterClass: string;
  pendingFileChipsMargin: string;
  filePaddingClass: string;
}

const STYLES: Record<AgentChatVariant, VariantStyles> = {
  drawer: {
    messageSize: 'sm',
    compactSubComponents: true,
    userBubbleClass: 'max-w-[80%] rounded-md px-4 py-3 bg-muted text-foreground',
    assistantBubbleClass: 'max-w-full text-foreground',
    outerContainerClass: 'flex-1 flex flex-col min-h-0',
    messagesInnerWrapper: '',
    messagesSpacing: 'space-y-7 p-4',
    emptyStateDefault: (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <MessageSquare className="w-12 h-12 text-muted-foreground/40 mb-3" />
        <p className="text-sm text-muted-foreground">
          Ask me anything about your programs, vendors, or requirements.
        </p>
        <p className="text-xs text-muted-foreground/70 mt-2">
          I can help you navigate, search, and make edits.
        </p>
      </div>
    ),
    centerEventCards: false,
    suggestionsPanelClass: 'px-4 py-2 shrink-0',
    inputAreaClass: 'p-4 pt-2 shrink-0',
    inputInnerWrapper: '',
    textareaClass:
      'flex-1 bg-transparent border-0 text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-0 disabled:opacity-50 resize-none overflow-hidden',
    textareaMinHeight: 24,
    textareaMaxHeight: 120,
    textareaPlaceholder: 'How can I help?',
    dragOverlayTextClass: 'text-base font-medium text-foreground',
    timestampMarginClass: 'text-xs mt-2 text-muted-foreground',
    attachmentsMarginClass: 'mt-1.5 flex flex-wrap gap-1',
    attachmentChipClass: 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs',
    fileChipOuterClass: 'inline-flex items-center gap-1 px-2 py-1 bg-muted text-muted-foreground rounded-full text-xs border border-border',
    pendingFileChipsMargin: 'flex flex-wrap gap-1.5 mb-2',
    filePaddingClass: 'max-w-[100px] truncate',
  },
  full: {
    messageSize: 'base',
    compactSubComponents: false,
    userBubbleClass: 'max-w-3xl rounded-md px-6 py-4 bg-muted text-foreground',
    assistantBubbleClass: 'max-w-3xl rounded-md px-6 py-4 bg-card border border-border text-foreground shadow-sm',
    outerContainerClass: 'flex-1 flex flex-col min-h-0',
    messagesInnerWrapper: 'max-w-5xl mx-auto',
    messagesSpacing: 'space-y-4 px-8 py-6',
    emptyStateDefault: (
      <div className="flex flex-col items-center justify-center h-full text-center py-12">
        <MessageSquare className="w-12 h-12 text-muted-foreground mb-3" />
        <p className="text-lg text-muted-foreground">
          Ask me anything about your programs, vendors, or requirements.
        </p>
      </div>
    ),
    centerEventCards: true,
    suggestionsPanelClass: 'px-8 py-3 border-t border-border shrink-0 bg-muted/40',
    inputAreaClass: 'px-8 py-6 border-t border-border shrink-0 bg-background',
    inputInnerWrapper: 'max-w-5xl mx-auto',
    textareaClass:
      'flex-1 bg-transparent border-0 text-foreground placeholder:text-muted-foreground text-base focus:outline-none focus:ring-0 disabled:opacity-50 resize-none overflow-hidden',
    textareaMinHeight: 28,
    textareaMaxHeight: 160,
    textareaPlaceholder: 'How can I help?',
    dragOverlayTextClass: 'text-lg font-medium text-foreground',
    timestampMarginClass: 'text-xs mt-2 text-muted-foreground',
    attachmentsMarginClass: 'mt-2 flex flex-wrap gap-1.5',
    attachmentChipClass: 'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs',
    fileChipOuterClass: 'inline-flex items-center gap-1.5 px-3 py-1.5 bg-muted text-muted-foreground rounded-full text-xs border border-border',
    pendingFileChipsMargin: 'flex flex-wrap gap-2 mb-3',
    filePaddingClass: 'max-w-[150px] truncate',
  },
};

const AgentChatView: React.FC<AgentChatViewProps> = ({
  variant,
  messages,
  isStreaming,
  viewSuggestions,
  actionSuggestions,
  pendingJobs,
  uploads,
  onToggleUploadContext,
  onSend,
  onUndo,
  onDismissSuggestion,
  onDismissActionSuggestion,
  onNavigateToView,
  emptyState,
  disabled,
}) => {
  const styles = STYLES[variant];

  const [input, setInput] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const { containerRef, contentRef, showJumpToLatest, jumpToLatest } = useStickToBottom();
  const dragCounterRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) {
      setPendingFiles((prev) => [...prev, ...files]);
    }
    e.target.value = '';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setPendingFiles((prev) => [...prev, ...files]);
    }
  };

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = () => {
    if (!input.trim() || isStreaming || disabled) return;
    const filesToSend = pendingFiles.length > 0 ? [...pendingFiles] : undefined;
    if (filesToSend) {
      setPendingFiles([]);
    }
    onSend(input, filesToSend);
    setInput('');
    // Sending is an explicit request to see what comes back.
    jumpToLatest();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const resolvedEmptyState = emptyState ?? styles.emptyStateDefault;

  const placeholder =
    pendingJobs.length > 0
      ? `Conversation held while waiting for ${pendingJobs[0].kindDisplay.toLowerCase()}…`
      : styles.textareaPlaceholder;

  const renderMessage = (message: AgentMessage) => {
    // Never render an empty user bubble.
    if (message.role === 'user' && !message.attachments?.length && !message.content.trim()) {
      return null;
    }
    if (message.role === 'event' && message.eventPayload) {
      if (styles.centerEventCards) {
        return (
          <div key={message.id} className="flex justify-center">
            <div className="max-w-2xl w-full">
              <EventCard payload={message.eventPayload} timestamp={message.timestamp} />
            </div>
          </div>
        );
      }
      return (
        <EventCard
          key={message.id}
          payload={message.eventPayload}
          timestamp={message.timestamp}
          compact
        />
      );
    }

    return (
      <div
        key={message.id}
        className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
      >
        <div
          className={message.role === 'user' ? styles.userBubbleClass : styles.assistantBubbleClass}
        >
          {message.segments && message.segments.length > 0 ? (
            message.segments.map((seg: ContentSegment, idx: number) => {
              if (seg.type === 'text' && seg.text) {
                return (
                  <div key={`seg-${idx}`}>
                    <MarkdownMessage
                      content={seg.text}
                      size={styles.messageSize}
                    />
                  </div>
                );
              }
              if (seg.type === 'tool_call') {
                if (seg.toolCall.hidden) return null;
                return (
                  <ExpandableToolPill
                    key={seg.toolCall.id || `seg-tool-${idx}`}
                    toolCall={seg.toolCall}
                    compact={styles.compactSubComponents}
                  />
                );
              }
              return null;
            })
          ) : (
            <>
              <MarkdownMessage
                content={message.content}
                size={styles.messageSize}
              />
              {message.toolCalls && message.toolCalls.length > 0 && (() => {
                const visible = message.toolCalls.filter((tc) => !tc.hidden);
                if (visible.length === 0) return null;
                return (
                  <div className={variant === 'drawer' ? 'mt-1.5 space-y-0.5' : 'mt-2 space-y-1'}>
                    {visible.map((tc, idx) => (
                      <ExpandableToolPill
                        key={tc.id || `tool-${idx}`}
                        toolCall={tc}
                        compact={styles.compactSubComponents}
                      />
                    ))}
                  </div>
                );
              })()}
            </>
          )}

          {message.editIds && message.editIds.length > 0 && (
            <div className={variant === 'drawer' ? 'mt-2 space-y-1' : 'mt-3 space-y-1'}>
              {message.editIds.map((editId, idx) => (
                <button
                  key={editId || `edit-${idx}`}
                  onClick={() => onUndo(editId)}
                  className="flex items-center gap-1 text-xs opacity-75 hover:opacity-100 transition-opacity"
                >
                  <RotateCcw className="w-3 h-3" />
                  Undo
                </button>
              ))}
            </div>
          )}

          {message.attachments && message.attachments.length > 0 && (
            <div className={styles.attachmentsMarginClass}>
              {message.attachments.map((name, idx) => (
                <div
                  key={`att-${idx}`}
                  className={`${styles.attachmentChipClass} bg-background/70 text-muted-foreground border border-border`}
                >
                  <Paperclip className="w-3 h-3" />
                  {name}
                </div>
              ))}
            </div>
          )}

          <p className={styles.timestampMarginClass}>
            {new Date(message.timestamp).toLocaleTimeString()}
            {message.isStreaming && ' • typing...'}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div
      className={styles.outerContainerClass}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-accent/40 border-2 border-dashed border-border rounded-lg pointer-events-none">
          <div className={styles.dragOverlayTextClass}>Drop files here to attach</div>
        </div>
      )}

      {/* Messages */}
      <div className="relative flex-1 min-h-0 flex flex-col">
        <div ref={containerRef} className="flex-1 min-h-0 overflow-y-auto">
          <div
            ref={contentRef}
            className={`${styles.messagesSpacing} ${styles.messagesInnerWrapper}`}
          >
            {messages.length === 0 ? (
              resolvedEmptyState
            ) : (
              <>
                {messages.map(renderMessage)}
                {pendingJobs.map((job) => {
                  if (variant === 'drawer') {
                    return <PendingJobCard key={`pending-${job.jobId}`} job={job} compact />;
                  }
                  return (
                    <div key={`pending-${job.jobId}`} className="flex justify-start">
                      <div className="max-w-3xl w-full">
                        <PendingJobCard job={job} />
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
        <JumpToLatest visible={showJumpToLatest} onClick={jumpToLatest} />
      </div>

      {/* View suggestions */}
      {viewSuggestions.length > 0 && (
        <div className={styles.suggestionsPanelClass}>
          <div className={styles.inputInnerWrapper}>
            <div className="space-y-2">
              {viewSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => {
                    onNavigateToView(suggestion);
                    onDismissSuggestion(index);
                  }}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-accent hover:bg-accent/70 border border-border rounded-lg text-sm text-foreground transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <ExternalLink className="w-4 h-4" />
                    {suggestion.label || `View ${suggestion.view}`}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDismissSuggestion(index);
                    }}
                    className="p-1 hover:bg-muted rounded"
                    title="Dismiss"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Action suggestions */}
      {actionSuggestions.length > 0 && (
        <div className={styles.suggestionsPanelClass}>
          <div className={styles.inputInnerWrapper}>
            <div className="space-y-2">
              {actionSuggestions.map((suggestion, index) => (
                <ActionSuggestionCard
                  key={`action-${index}`}
                  suggestion={suggestion}
                  onDismiss={() => onDismissActionSuggestion(index)}
                  compact={styles.compactSubComponents}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Input */}
      <div className={styles.inputAreaClass}>
        <div className={styles.inputInnerWrapper}>
          {uploads && uploads.length > 0 && (
            <div className={styles.pendingFileChipsMargin} aria-label="Files in this conversation">
              {uploads.map((upload) => (
                <UploadChip
                  key={upload.id}
                  upload={upload}
                  chipClass={styles.fileChipOuterClass}
                  nameClass={styles.filePaddingClass}
                  onToggle={onToggleUploadContext}
                  disabled={disabled}
                />
              ))}
            </div>
          )}
          {pendingFiles.length > 0 && (
            <div className={styles.pendingFileChipsMargin}>
              {pendingFiles.map((file, i) => (
                <div key={i} className={styles.fileChipOuterClass}>
                  <Paperclip className="w-3 h-3" />
                  <span className={styles.filePaddingClass}>{file.name}</span>
                  <button
                    onClick={() => removePendingFile(i)}
                    className="ml-0.5 hover:text-destructive transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFilePick}
            className="hidden"
          />
          <div className="flex items-center gap-2 rounded-lg border border-input bg-background p-3 shadow-xs">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={isStreaming || disabled}
              rows={1}
              className={styles.textareaClass}
              style={{
                minHeight: `${styles.textareaMinHeight}px`,
                maxHeight: `${styles.textareaMaxHeight}px`,
              }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height =
                  Math.min(target.scrollHeight, styles.textareaMaxHeight) + 'px';
              }}
            />
            <div className="flex items-center gap-1 shrink-0 self-end">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isStreaming || disabled}
                className="flex items-center justify-center w-10 h-10 rounded-full text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40 transition-colors"
                title="Attach files"
              >
                <Paperclip className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={!input.trim() || isStreaming || disabled}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:hover:bg-primary transition-colors"
                title="Send message"
              >
                {isStreaming ? <Spinner size="sm" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * A file already uploaded to the conversation, with its "Program context" ↔
 * "Chat only" switch. Program context means the file is promoted to a program
 * context document and extracted into requirements (once the conversation has
 * a program); chat only means just the agent reads it.
 */
const UploadChip: React.FC<{
  upload: AgentUpload;
  chipClass: string;
  nameClass: string;
  onToggle?: (uploadId: string, useAsProgramContext: boolean) => void;
  disabled?: boolean;
}> = ({ upload, chipClass, nameClass, onToggle, disabled }) => {
  // Backend default is true; older payloads without the field read as on.
  const isProgramContext = upload.use_as_program_context !== false;
  const doc = upload.program_context_document;
  const extracting = isUploadExtracting(upload);
  const failed = doc?.extraction_state === 'failed';
  const extracted = doc?.extraction_state === 'completed';

  return (
    <div className={chipClass}>
      <Paperclip className="w-3 h-3 shrink-0" />
      <span className={nameClass} title={upload.title}>{upload.title}</span>
      {isProgramContext && extracting && (
        <span className="inline-flex shrink-0" title="Extracting into the program…">
          <Loader2 className="w-3 h-3 animate-spin" aria-label="Extracting into the program" />
        </span>
      )}
      {isProgramContext && extracted && (
        <span className="inline-flex shrink-0" title="Extracted into the program">
          <CheckCircle2 className="w-3 h-3 text-success" aria-label="Extracted into the program" />
        </span>
      )}
      {isProgramContext && failed && (
        <span className="inline-flex shrink-0" title="Extraction failed">
          <AlertCircle className="w-3 h-3 text-destructive" aria-label="Extraction failed" />
        </span>
      )}
      <button
        type="button"
        role="switch"
        aria-checked={isProgramContext}
        disabled={!onToggle || disabled}
        onClick={() => onToggle?.(upload.id, !isProgramContext)}
        title={
          isProgramContext
            ? 'Used as program context: its contents are extracted into the program. Click to keep it chat only.'
            : 'Chat only: the agent can read it, but it is not added to the program. Click to use it as program context.'
        }
        className={`ml-0.5 shrink-0 rounded-full px-1.5 py-px text-[10px] font-medium leading-4 transition-colors disabled:cursor-default ${
          isProgramContext
            ? 'bg-foreground text-background hover:opacity-80'
            : 'border border-border bg-background text-muted-foreground hover:text-foreground'
        }`}
      >
        {isProgramContext ? 'Program context' : 'Chat only'}
      </button>
    </div>
  );
};

export default AgentChatView;
